import React from 'react';
import { Coffee, Plus } from 'lucide-react';
import { EventRow } from './EventRow';
import { Event } from '@/types/zod.schemas';
import { UI } from '@/shared/design-system/tactical-ui';
import { motion, AnimatePresence } from 'framer-motion';

// Modular Stores (v4)
import { useHistoryStore } from '@stores/v4/useHistoryStore';
import { useComposerStore } from '@stores/v4/useComposerStore';
import { useUIStore } from '@stores/v4/useUIStore';
import { HistoryOrchestrator } from '@services/history-orchestrator';

interface MasterAgendaViewProps {
    events: Event[];
    onSelectEvent: (event: Event) => void;
    onDismissGhost?: (window: string) => void;
    selectedDate: Date;
}

const AddEventButton: React.FC<{ onAdd: () => void }> = ({ onAdd }) => (
    <button 
        onClick={onAdd}
        style={{ 
            all: 'unset', width: '100%', height: '52px', 
            backgroundColor: 'rgba(242, 155, 17, 0.06)', 
            border: '1px dashed rgba(242, 155, 17, 0.3)', 
            borderRadius: '14px', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', 
            gap: '10px', cursor: 'pointer', marginBottom: '16px', transition: 'all 0.2s ease' 
        }}
        className="hover-glow active:scale-[0.98]"
    >
        <Plus size={18} color="#f29b11" strokeWidth={3.5} />
        <span style={{ fontSize: '12px', fontWeight: '950', color: '#f29b11', textTransform: 'uppercase', letterSpacing: '2.5px', marginLeft: '2px' }}>Add Event</span>
    </button>
);

/**
 * MasterAgendaView: The tactical event list.
 * HARDENED: Using HistoryOrchestrator for async hydration.
 * UPDATED: Pass onDismissGhost to EventRow.
 */
export const MasterAgendaView: React.FC<MasterAgendaViewProps> = ({
    events,
    onSelectEvent,
    onDismissGhost,
    selectedDate
}) => {
    const expandedActivityId = useHistoryStore(state => state.expandedActivityId);
    const setExpandedActivityId = useHistoryStore(state => state.setExpandedActivityId);
    const activeHistorySession = useHistoryStore(state => state.activeHistorySession);
    const analyzingEvents = useHistoryStore(state => state.analyzingEvents);
    
    const { openComposer } = useComposerStore();
    const { isLoading } = useUIStore();

    const handleToggleExpansion = (event: Event) => {
        const effectiveType = event.type;
        if (effectiveType !== 'PERFORMANCE' && effectiveType !== 'MOBILITY') return;
        if (event.status !== 'COMPLETED' && event.status !== 'ACTIVE') return;

        if (expandedActivityId === event.id) setExpandedActivityId(null);
        else {
            setExpandedActivityId(event.id);
            if (event.completed_session_id) {
                HistoryOrchestrator.fetchSessionDetails(event.completed_session_id);
            }
        }
    };

    const handleAddEvent = () => {
        openComposer(selectedDate);
    };

    const dateKey = selectedDate.toISOString().split('T')[0];

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <AddEventButton onAdd={handleAddEvent} />
            
            <AnimatePresence mode="wait">
                <motion.div
                    key={dateKey}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                >
                    {events.length === 0 ? (
                        <div style={{ padding: '60px 20px', textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.01)', border: '1px dashed #222', borderRadius: '18px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <Coffee size={42} color="#333" strokeWidth={1.5} style={{ marginBottom: '16px', opacity: 0.6 }} />
                            <p style={{ fontSize: '11px', color: '#555', fontWeight: '900', textTransform: 'uppercase', margin: 0, letterSpacing: '2px' }}>No Activity Logged</p>
                        </div>
                    ) : (
                        events.map((event, index) => (
                            <EventRow 
                                key={event.id || `event-${index}`}
                                event={event}
                                isExpanded={expandedActivityId === event.id}
                                onToggle={() => handleToggleExpansion(event)}
                                onEdit={onSelectEvent}
                                onDismissGhost={onDismissGhost}
                                isLoading={isLoading}
                                isAnalyzing={analyzingEvents.has(event.id)}
                                details={activeHistorySession}
                            />
                        ))
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};
